// GDPR-compliant Google Analytics utility
// Now with Consent Mode for cookieless tracking

import { getCookiePreferences } from './cookieManager';

const GA_MEASUREMENT_ID = 'G-GBHGGRPFVR';
const GA_ADS_ID = 'AW-17919477997';

let isAnalyticsLoaded = false;
let isAdsLoaded = false;

// Load Google Analytics script dynamically with Consent Mode
// NOTE: GA4 is now pre-loaded in index.html for immediate tracking
// This function now just verifies it's loaded and configures linker
export const loadGoogleAnalytics = () => {
  return new Promise((resolve, reject) => {
    if (isAnalyticsLoaded) {
      resolve();
      return;
    }

    try {
      // Check if gtag is already loaded from index.html
      if (window.gtag) {
        console.log('Google Analytics already loaded from index.html');
        isAnalyticsLoaded = true;
        
        // Configure cross-domain linker for CloudFront and non-www domain
        window.gtag('set', 'linker', {
          'domains': ['originselect.com', 'www.originselect.com', 'd3d8378t728e1f.cloudfront.net'],
          'accept_incoming': true
        });
        
        resolve();
        return;
      }

      // Fallback: Load GA4 dynamically if not already present
      window.dataLayer = window.dataLayer || [];
      
      // Define gtag function
      function gtag() {
        window.dataLayer.push(arguments);
      }
      window.gtag = gtag;

      // Load the Google Analytics script
      const script = document.createElement('script');
      script.async = true;
      script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`;
      
      script.onload = () => {
        // Configure Google Analytics with Consent Mode
        gtag('js', new Date());
        
        // Set default consent state (before user interaction)
        gtag('consent', 'default', {
          'analytics_storage': 'denied',
          'ad_storage': 'denied',
          'ad_user_data': 'denied',
          'ad_personalization': 'denied',
          'functionality_storage': 'granted',
          'personalization_storage': 'denied',
          'security_storage': 'granted'
        });
        
        gtag('config', GA_MEASUREMENT_ID, {
          // Privacy-friendly settings
          anonymize_ip: true,
          send_page_view: true,
          cookie_flags: 'SameSite=None;Secure',
          // Cross-domain tracking configuration
          linker: {
            domains: ['originselect.com', 'www.originselect.com', 'd3d8378t728e1f.cloudfront.net'],
            accept_incoming: true
          }
        });
        
        isAnalyticsLoaded = true;
        console.log('Google Analytics loaded with Consent Mode (fallback)');
        resolve();
      };
      
      script.onerror = () => {
        reject(new Error('Failed to load Google Analytics'));
      };
      
      document.head.appendChild(script);
    } catch (error) {
      reject(error);
    }
  });
};

// Load Google Ads script dynamically
export const loadGoogleAds = () => {
  return new Promise((resolve, reject) => {
    if (isAdsLoaded) {
      resolve();
      return;
    }

    try {
      // Initialize dataLayer if it doesn't exist
      window.dataLayer = window.dataLayer || [];
      
      // Define gtag function if not already defined
      if (!window.gtag) {
        function gtag() {
          window.dataLayer.push(arguments);
        }
        window.gtag = gtag;
      }

      // Load the Google Ads script
      const script = document.createElement('script');
      script.async = true;
      script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_ADS_ID}`;
      
      script.onload = () => {
        // Configure Google Ads
        window.gtag('js', new Date());
        window.gtag('config', GA_ADS_ID);
        
        isAdsLoaded = true;
        console.log('Google Ads loaded with user consent');
        resolve();
      };
      
      script.onerror = () => {
        reject(new Error('Failed to load Google Ads'));
      };
      
      document.head.appendChild(script);
    } catch (error) {
      reject(error);
    }
  });
};

// Initialize analytics based on user preferences
export const initializeAnalytics = async () => {
  const preferences = getCookiePreferences();
  
  try {
    // Always load Google Analytics (it uses consent mode)
    await loadGoogleAnalytics();
    
    // Update consent based on user preferences
    updateAnalyticsConsent(preferences.analytics, preferences.marketing);
    
    if (preferences.marketing) {
      await loadGoogleAds();
    }
  } catch (error) {
    console.error('Error initializing analytics:', error);
  }
};

// Update consent when user makes a choice
// Uses Consent Mode v2 with additional signals
export const updateAnalyticsConsent = (analyticsAllowed, marketingAllowed) => {
  if (window.gtag) {
    window.gtag('consent', 'update', {
      'analytics_storage': analyticsAllowed ? 'granted' : 'denied',
      'ad_storage': marketingAllowed ? 'granted' : 'denied',
      'ad_user_data': marketingAllowed ? 'granted' : 'denied',
      'ad_personalization': marketingAllowed ? 'granted' : 'denied',
      'personalization_storage': analyticsAllowed ? 'granted' : 'denied'
    });
    
    console.log('Analytics consent updated (Consent Mode v2):', { 
      analytics: analyticsAllowed ? 'granted' : 'denied',
      marketing: marketingAllowed ? 'granted' : 'denied'
    });
  }
};

// Enhanced tracking function that works with consent mode
export const trackEventWithConsent = (eventName, parameters = {}) => {
  // This will work even without full consent due to consent mode
  // Check either isAnalyticsLoaded (set by loadGoogleAnalytics) OR
  // window.__GA4_READY__ (set in index.html when GA4 config runs) to avoid
  // dropping events fired before the AnalyticsInitializer effect resolves
  const ga4Ready = isAnalyticsLoaded || window.__GA4_READY__;

  if (window.gtag && ga4Ready) {
    window.gtag('event', eventName, parameters);
    console.log('Event tracked with consent mode:', eventName, parameters);
  } else {
    console.warn('Event dropped - GA4 not ready:', eventName, {
      hasGtag: !!window.gtag,
      isAnalyticsLoaded,
      ga4Ready: window.__GA4_READY__
    });
  }
};

// Track page views with consent mode (works even without full consent)
// Uses window.__GA4_READY__ flag set in index.html to avoid race condition
export const trackPageViewWithConsent = (page_title, page_location) => {
  // Check either isAnalyticsLoaded (set by loadGoogleAnalytics) OR
  // window.__GA4_READY__ (set in index.html when GA4 config runs)
  const ga4Ready = isAnalyticsLoaded || window.__GA4_READY__;
  
  if (window.gtag && ga4Ready) {
    window.gtag('event', 'page_view', {
      page_title,
      page_location,
      page_path: window.location.pathname,
      // Include referrer for better Google Search attribution
      page_referrer: document.referrer || ''
    });
  } else {
    // Debug: log if pageview was dropped
    console.warn('GA4 pageview not sent - gtag:', !!window.gtag, 'ready:', ga4Ready);
  }
};

// Safe analytics tracking function (backwards compatibility)
export const trackEvent = (eventName, parameters = {}) => {
  const preferences = getCookiePreferences();
  
  // Only track if analytics are enabled and gtag is available
  if (preferences.analytics && window.gtag && isAnalyticsLoaded) {
    window.gtag('event', eventName, parameters);
  } else if (window.gtag && isAnalyticsLoaded) {
    // Use consent mode tracking even without full consent
    trackEventWithConsent(eventName, parameters);
  }
};

// Safe page view tracking (backwards compatibility)
export const trackPageView = (page_title, page_location) => {
  const preferences = getCookiePreferences();
  
  if (preferences.analytics && window.gtag && isAnalyticsLoaded) {
    window.gtag('event', 'page_view', {
      page_title,
      page_location,
      page_path: window.location.pathname
    });
  } else if (window.gtag && isAnalyticsLoaded) {
    // Use consent mode tracking even without full consent
    trackPageViewWithConsent(page_title, page_location);
  }
};

// Safe conversion tracking
export const trackConversion = (conversionId, parameters = {}) => {
  const preferences = getCookiePreferences();
  
  if (preferences.marketing && window.gtag && isAdsLoaded) {
    window.gtag('event', 'conversion', {
      send_to: conversionId,
      ...parameters
    });
  }
};

// Safe user identification (for logged-in users)
export const setUserId = (userId) => {
  const preferences = getCookiePreferences();
  
  if (preferences.analytics && window.gtag && isAnalyticsLoaded) {
    window.gtag('config', GA_MEASUREMENT_ID, {
      user_id: userId
    });
  }
};

// Remove analytics cookies and scripts when consent is withdrawn
export const removeAnalytics = () => {
  // Remove Google Analytics cookies
  const gaCookies = ['_ga', '_ga_' + GA_MEASUREMENT_ID.replace('G-', ''), '_gid', '_gat'];
  gaCookies.forEach(cookie => {
    document.cookie = `${cookie}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; domain=.${window.location.hostname}`;
    document.cookie = `${cookie}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
  });
  
  // Remove Google Ads cookies
  const adsCookies = ['_gcl_au', '_gcl_dc', '_gcl_aw'];
  adsCookies.forEach(cookie => {
    document.cookie = `${cookie}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; domain=.${window.location.hostname}`;
    document.cookie = `${cookie}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
  });
  
  // Reset loaded states
  isAnalyticsLoaded = false;
  isAdsLoaded = false;
  
  // Clear gtag if it exists
  if (window.gtag) {
    delete window.gtag;
  }
  
  console.log('Analytics tracking disabled and cookies removed');
};

// Utility to check if analytics is enabled
export const isAnalyticsEnabled = () => {
  const preferences = getCookiePreferences();
  return preferences.analytics;
};

// Utility to check if marketing is enabled
export const isMarketingEnabled = () => {
  const preferences = getCookiePreferences();
  return preferences.marketing;
};
